import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, TrendingUp, Users, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { caseStudies } from '../data/mock';

const CaseStudies = () => {
  return (
    <div className="min-h-screen bg-slate-950 pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-600 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/">
            <Button variant="ghost" className="text-cyan-400 hover:text-cyan-300 mb-8 group">
              <ArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Home
            </Button>
          </Link>

          <div className="text-center max-w-3xl mx-auto">
            <span className="text-cyan-400 font-semibold text-sm uppercase tracking-wider">Case Studies</span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mt-4 mb-6">
              Success Stories That
              <span className="block bg-gradient-to-r from-cyan-400 to-teal-500 bg-clip-text text-transparent">
                Drive Results
              </span>
            </h1>
            <p className="text-xl text-gray-400">
              Discover how we've helped golf businesses achieve remarkable growth through strategic marketing initiatives
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <Card
                key={study.id}
                className="bg-slate-900/50 border-slate-800 hover:border-cyan-500/50 backdrop-blur-sm transition-all duration-300 hover:transform hover:scale-[1.02] group overflow-hidden"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
                  
                  {/* Badges */}
                  <div className="absolute top-4 left-4 flex gap-2">
                    <Badge className="bg-cyan-500/90 text-white border-0">
                      {study.category}
                    </Badge>
                    <Badge variant="outline" className="bg-slate-900/90 text-white border-slate-700">
                      {study.year}
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-8">
                  {/* Client */}
                  <div className="flex items-center space-x-2 text-cyan-400 mb-3">
                    <Users size={16} />
                    <span className="text-sm font-medium">{study.client}</span>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">
                    {study.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-400 mb-6 leading-relaxed">
                    {study.description}
                  </p>

                  {/* Results */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-green-400 font-semibold mb-3">
                      <TrendingUp size={18} />
                      <span className="text-sm uppercase tracking-wider">Key Results</span>
                    </div>
                    {study.results.map((result, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-300">{result}</span>
                      </div>
                    ))}
                  </div>

                  {/* View Details Button */}
                  <Button
                    variant="outline"
                    className="w-full mt-6 border-cyan-500 text-cyan-400 hover:bg-cyan-500 hover:text-white transition-all duration-300"
                  >
                    View Full Case Study
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Load More (placeholder for future) */}
          <div className="text-center mt-12">
            <p className="text-gray-400 mb-4">Want to see more success stories?</p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white font-semibold px-8 py-3 rounded-lg">
                Let's Create Your Success Story
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp size={32} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">150%</div>
              <div className="text-gray-400">Average Growth</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users size={32} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">50+</div>
              <div className="text-gray-400">Happy Clients</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <DollarSign size={32} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">3.5x</div>
              <div className="text-gray-400">Average ROI</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Calendar size={32} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-cyan-400 mb-2">15+</div>
              <div className="text-gray-400">Years Experience</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
